import { useEffect, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { capabilityOf, type CapabilityId } from '@dale/plans';

import { PlanLocked } from '../components/PlanLocked';
import { accessTo } from '../components/shell/navAccess';
import { useBusiness } from '../lib/useBusiness';
import { useFeatures } from '../lib/useFeatures';

type RequirePlanProps = {
  children: ReactNode;
  /** The section of the plan catalog (@dale/plans) this route is. */
  capability: CapabilityId;
};

/**
 * Nests inside RequireAuth (auth first, then plan) on every plan-gated route.
 * Every plan sees the same CRM, so a section the plan does not include shows
 * its lock in place (what it does, the plan that opens it) instead of
 * bouncing home. A switch a Dale operator set wins over the plan, from the
 * server's effective map in `/v1/business/me`: on opens the section for a
 * lower plan; off, for a plan that includes it, sends home quietly.
 *
 * useBusiness() and useFeatures() answer `undefined` and not-ready while their
 * fetch is in flight. Until what it needs has arrived (the business, and the
 * features for a section an operator can switch) this renders the children
 * and never flashes a lock or a bounce; the server refuses the data anyway.
 */
export function RequirePlan({ children, capability }: RequirePlanProps) {
  const business = useBusiness();
  const { features, ready } = useFeatures();
  const navigate = useNavigate();
  // A section with no switch is the plan's call alone, so it decides as soon as the business loads; one a
  // Dale operator can switch waits for the features. A loaded business with no plan reads as Base, like the server.
  const decided = business !== undefined && (ready || !capabilityOf(capability).feature);
  const access = decided ? accessTo(capability, business.plan ?? 'base', features) : undefined;
  const off = access?.kind === 'off';

  useEffect(() => {
    if (off) void navigate({ to: '/', replace: true });
  }, [off, navigate]);

  if (!access || access.kind === 'open') return <>{children}</>;
  if (access.kind === 'locked') return <PlanLocked capability={capability} />;
  return null;
}
